import type { AdminOrder, AdminSummary, Category, CreatedOrder, InstallmentOption, OrderQuote, Product, PublicOrder } from "@/types/api";

const API_URL = import.meta.env.VITE_API_URL ?? "";
const TENANT_CODE = import.meta.env.VITE_TENANT_CODE ?? "certi-spark-store";
const ADMIN_TOKEN_KEY = "certi_admin_token";

async function request<T>(path: string, init?: RequestInit) {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {})
      }
    });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    throw new Error(`Falha de conexão ao servidor: ${msg}`);
  }

  const text = await response.text();

  // If there's a body, try to parse it as JSON. If that fails, surface the
  // HTTP status plus a short preview so we can tell whether we got a
  // nginx/cloudflare HTML error page vs. a backend error vs. an empty body.
  let data: (T & { error?: string }) | null = null;
  if (text) {
    try {
      data = JSON.parse(text) as T & { error?: string };
    } catch {
      const preview = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 140);
      const hint = response.status >= 500
        ? "o servidor retornou uma página de erro (provável 502/504 do proxy ou timeout). Tente novamente em alguns segundos."
        : `resposta não-JSON (HTTP ${response.status}): ${preview || "(corpo vazio)"}`;
      throw new Error(`Servidor respondeu de forma inesperada — ${hint}`);
    }
  }

  if (!response.ok) {
    const serverMsg = data?.error;
    throw new Error(serverMsg ?? `Requisição falhou (HTTP ${response.status})`);
  }

  // Preserve explicit `null` responses from the backend (some endpoints
  // intentionally return null for absent resources, e.g. homepage-promotion
  // when no promotion is active). Previously we coalesced to `{}` here,
  // which meant callers got a truthy empty object and any code checking
  // `if (!value)` crashed when they tried to read real properties.
  // Only fall back to `{}` for truly empty bodies (HTTP 204, DELETE, etc).
  if (text.trim() === "null") return null as T;
  return (data ?? ({} as T)) as T;
}

export function formatCurrency(cents: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(cents / 100);
}

export function getTenantCode() {
  return TENANT_CODE;
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token: string) {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export async function getCatalog() {
  return request<{ products: Product[]; categories: Category[] }>("/api/public/products");
}

export interface SocialProof {
  googleReviews: { rating: number; totalRatings: number } | null;
  certificatesEmitted: number;
  customersServed: number;
}

export async function getSocialProof() {
  return request<SocialProof>("/api/public/social-proof");
}

export interface HomepagePromotion {
  id: string;
  name: string;
  description: string | null;
  scope: "GLOBAL" | "CATEGORY" | "PRODUCT";
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  badgeLabel: string | null;
  badgeColor: string | null;
  startsAt: string;
  endsAt: string;
}

export async function getHomepagePromotion() {
  return request<HomepagePromotion | null>("/api/public/homepage-promotion");
}

export interface BusinessInfo {
  operatingHours: { from: string; to: string; timezone: string };
  sameDayCutoff: { enabled: boolean; cutoff: string; timezone: string };
  saturday: { enabled: boolean; from?: string; to?: string };
  holidays: string[];
  emission: {
    sameDayEligible: boolean;
    promise: string;
    operatingHours: { from: string; to: string };
  };
}

export async function getBusinessInfo() {
  return request<BusinessInfo>("/api/public/business-info");
}

export async function getProduct(slug: string) {
  return request<Product>(`/api/public/products/${slug}`);
}

export async function quoteOrder(
  items: Array<{ productId: string; quantity: number }>,
  couponCode?: string | null
) {
  return request<OrderQuote>("/api/public/orders/quote", {
    method: "POST",
    body: JSON.stringify({
      tenantCode: TENANT_CODE,
      items,
      couponCode: couponCode || undefined,
    })
  });
}

export interface CouponClaimResult {
  code: string;
  discountLabel: string;
  expiresAt: string;
  minOrderCents: number;
}

export async function claimCoupon(email: string, source = "exit-intent-coupon") {
  return request<CouponClaimResult>("/api/public/coupons/claim", {
    method: "POST",
    body: JSON.stringify({ email, source }),
  });
}

export interface CouponValidateResult {
  valid: boolean;
  reason?: string;
  discountCents: number;
  coupon?: { code: string; discountType: "PERCENT" | "FIXED"; discountValue: number };
}

export async function validateCouponCode(code: string, subtotalCents: number) {
  return request<CouponValidateResult>("/api/public/coupons/validate", {
    method: "POST",
    body: JSON.stringify({ code, subtotalCents }),
  });
}

export async function getInstallmentOptions(totalCents: number) {
  return request<{ options: InstallmentOption[] }>(
    `/api/public/installments?total=${totalCents}`
  );
}

export async function createOrder(payload: Record<string, unknown>) {
  return request<CreatedOrder>("/api/public/orders", {
    method: "POST",
    body: JSON.stringify({
      tenantCode: TENANT_CODE,
      ...payload
    })
  });
}

export async function getOrder(orderCode: string, token: string) {
  return request<PublicOrder>(`/api/public/orders/${orderCode}?token=${encodeURIComponent(token)}`);
}

export async function lookupOrder(orderCode: string, email: string) {
  return request<{ publicToken: string }>("/api/public/orders/lookup", {
    method: "POST",
    body: JSON.stringify({ orderCode, email }),
  });
}

export interface ContactLeadPayload {
  nome: string;
  sobrenome: string;
  email: string;
  telefone: string;
  mensagem: string;
}

export async function submitContactLead(payload: ContactLeadPayload) {
  return request<{ ok: boolean }>("/api/public/leads/contact", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface PartnershipLeadPayload {
  nome: string;
  sobrenome: string;
  telefone: string;
  email: string;
  empresa: string;
  cidade: string;
  jaAtua: string;
}

export async function submitPartnershipLead(payload: PartnershipLeadPayload) {
  return request<{ ok: boolean }>("/api/public/leads/partnership", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type AdminLoginResponse =
  | {
      requires2fa: true;
      challengeToken: string;
    }
  | {
      requires2fa: false;
      token: string;
      user: { id: string; email: string; name: string; role: string };
    };

export async function adminLogin(email: string, password: string) {
  return request<AdminLoginResponse>("/api/admin/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function verify2faChallenge(challengeToken: string, code: string) {
  return request<{ token: string; user: { id: string; email: string; name: string; role: string } }>(
    "/api/admin/auth/2fa/verify",
    {
      method: "POST",
      body: JSON.stringify({ challengeToken, code }),
    }
  );
}

// 2FA management
export interface TwoFactorStatus {
  enabled: boolean;
  pendingSetup: boolean;
}

export async function getAdminTwoFactorStatus() {
  return request<TwoFactorStatus>("/api/admin/2fa/status", { headers: authHeaders() });
}

export async function startAdminTwoFactor() {
  return request<{ secret: string; otpauth: string; qrcodeDataUrl: string }>(
    "/api/admin/2fa/start",
    { method: "POST", headers: authHeaders() }
  );
}

export async function confirmAdminTwoFactor(code: string) {
  return request<{ enabled: boolean }>("/api/admin/2fa/confirm", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  });
}

export async function disableAdminTwoFactor() {
  return request<{ enabled: boolean }>("/api/admin/2fa/disable", {
    method: "POST",
    headers: authHeaders(),
  });
}

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getAdminDashboard() {
  return request<AdminSummary>("/api/admin/dashboard", {
    headers: authHeaders()
  });
}

export async function getAdminProducts(
  opts: { status?: string; search?: string; limit?: number; offset?: number } = {}
) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.search) params.set("search", opts.search);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; limit: number; offset: number; items: Product[] }>(
    `/api/admin/products${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export async function createAdminProduct(payload: Record<string, unknown>) {
  return request<{ id: string }>("/api/admin/products", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
}

export async function updateAdminProduct(id: string, payload: Record<string, unknown>) {
  return request<void>(`/api/admin/products/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload)
  });
}

export async function deleteAdminProduct(id: string) {
  return request<void>(`/api/admin/products/${id}`, {
    method: "DELETE",
    headers: authHeaders()
  });
}

// ── CRM: customers + interactions ────────────────────────────────────

export interface AdminCustomerRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  cpfCnpj: string;
  notes: string | null;
  tags: string[];
  createdAt: string;
  orderCount: number;
  lifetimeValueCents: number;
  lastInteractionAt: string | null;
  interactionCount: number;
  companyCount: number;
}

export interface CustomerCompany {
  id: string;
  customerId: string;
  name: string;
  cnpj: string;
  role: string | null;
  isPrimary: boolean;
  createdAt: string;
}

export interface ScheduledMessage {
  id: string;
  customerId: string | null;
  channel: "WHATSAPP" | "EMAIL";
  toPhone: string | null;
  toEmail: string | null;
  subject: string | null;
  body: string;
  scheduledFor: string;
  status: "PENDING" | "SENT" | "FAILED" | "CANCELLED";
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  templateId: string | null;
  createdAt: string;
}

export interface MessageTemplate {
  id: string;
  channel: "WHATSAPP" | "EMAIL";
  title: string;
  subject: string | null;
  body: string;
  variables: { key: string; label: string; defaultValue?: string }[];
  createdAt: string;
  updatedAt: string;
}

export type InteractionChannel = "WHATSAPP" | "EMAIL" | "SYSTEM" | "NOTE" | "PHONE";
export type InteractionDirection = "IN" | "OUT" | "SYSTEM";

export interface AdminInteraction {
  id: string;
  customerId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  channel: InteractionChannel;
  direction: InteractionDirection;
  subject: string | null;
  body: string | null;
  metadata: Record<string, unknown>;
  externalId: string | null;
  threadId: string | null;
  deliveryStatus: string | null;
  actorId: string | null;
  createdAt: string;
}

export interface AdminInteractionTimeline {
  summary: {
    total: number;
    lastAt: string | null;
    lastChannel: string | null;
  };
  items: AdminInteraction[];
}

export async function getAdminCustomers(
  opts: { search?: string; limit?: number; offset?: number } = {}
) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{
    total: number;
    limit: number;
    offset: number;
    items: AdminCustomerRow[];
  }>(`/api/admin/customers${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
}

export async function getAdminCustomerInteractions(id: string, channel?: InteractionChannel) {
  const qs = channel ? `?channel=${channel}` : "";
  return request<AdminInteractionTimeline>(
    `/api/admin/customers/${encodeURIComponent(id)}/interactions${qs}`,
    { headers: authHeaders() }
  );
}

export async function bulkDeleteAdminCustomers(ids: string[]) {
  return request<{ deleted: number }>(`/api/admin/customers/bulk-delete`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ ids }),
  });
}

export async function bulkDeleteAdminCustomerInteractions(customerId: string, ids: string[]) {
  return request<{ deleted: number }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/interactions/bulk-delete`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ ids }),
    }
  );
}

export async function createAdminCustomerNote(id: string, subject: string, body: string) {
  return request<{ id: string }>(
    `/api/admin/customers/${encodeURIComponent(id)}/interactions/note`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ subject, body }),
    }
  );
}

// ── CRM: contact edit ─────────────────────────────────────────────────

export interface UpdateCustomerPayload {
  name?: string;
  email?: string;
  phone?: string;
  cpfCnpj?: string;
  notes?: string | null;
  tags?: string[];
}

export async function updateAdminCustomer(id: string, patch: UpdateCustomerPayload) {
  return request<void>(`/api/admin/customers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
}

// ── CRM: company associations ─────────────────────────────────────────

export async function getAdminCustomerCompanies(id: string) {
  return request<CustomerCompany[]>(
    `/api/admin/customers/${encodeURIComponent(id)}/companies`,
    { headers: authHeaders() }
  );
}

export async function upsertAdminCustomerCompany(
  customerId: string,
  input: { id?: string; name: string; cnpj: string; role?: string | null; isPrimary?: boolean }
) {
  return request<CustomerCompany>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/companies`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(input),
    }
  );
}

export async function deleteAdminCustomerCompany(customerId: string, companyId: string) {
  return request<void>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/companies/${encodeURIComponent(companyId)}`,
    { method: "DELETE", headers: authHeaders() }
  );
}

// ── CRM: outbound sends ───────────────────────────────────────────────

export async function sendAdminWhatsAppToCustomer(customerId: string, text: string) {
  return request<{ ok: boolean; messageId?: string }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/send/whatsapp`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify({ text }) }
  );
}

export async function sendAdminEmailToCustomer(
  customerId: string,
  input: { subject: string; html: string; text?: string }
) {
  return request<{ ok: boolean; messageId?: string }>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/send/email`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify(input) }
  );
}

// ── CRM: payment links ────────────────────────────────────────────────

export interface CrmPaymentLinkResult {
  invoiceUrl: string;
  amountCents: number;
  providerPaymentId: string;
  productNames: string[];
}

export async function createCrmPaymentLink(
  customerId: string,
  input: {
    productIds: string[];
    discountCents?: number;
    payer: "CPF" | "CNPJ";
    companyId?: string | null;
    dueDate?: string;
    note?: string;
  }
) {
  return request<CrmPaymentLinkResult>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/payment-link`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify(input) }
  );
}

// ── CRM: scheduled messages ───────────────────────────────────────────

export async function listScheduledMessages(customerId: string) {
  return request<ScheduledMessage[]>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/scheduled-messages`,
    { headers: authHeaders() }
  );
}

export async function createScheduledMessage(
  customerId: string,
  input: {
    channel: "WHATSAPP" | "EMAIL";
    body: string;
    subject?: string;
    scheduledFor: string;
    templateId?: string;
  }
) {
  return request<ScheduledMessage>(
    `/api/admin/customers/${encodeURIComponent(customerId)}/scheduled-messages`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify(input) }
  );
}

export async function cancelScheduledMessage(id: string) {
  return request<void>(`/api/admin/scheduled-messages/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// ── CRM: templates ────────────────────────────────────────────────────

export async function listMessageTemplates(channel?: "WHATSAPP" | "EMAIL") {
  const qs = channel ? `?channel=${channel}` : "";
  return request<MessageTemplate[]>(`/api/admin/message-templates${qs}`, {
    headers: authHeaders(),
  });
}

export async function upsertMessageTemplate(input: {
  id?: string;
  channel: "WHATSAPP" | "EMAIL";
  title: string;
  subject?: string;
  body: string;
  variables?: { key: string; label: string; defaultValue?: string }[];
}) {
  return request<MessageTemplate>(`/api/admin/message-templates`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
}

export async function deleteMessageTemplate(id: string) {
  return request<void>(`/api/admin/message-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// ── Analytics ───────────────────────────────────────────────────────

export interface AnalyticsOverview {
  range: { from: string; to: string; days: number };
  totals: {
    visitors: number;
    sessions: number;
    pageviews: number;
    events: number;
    bounceRate: number;
    avgSessionDurationSeconds: number;
    conversions: number;
    conversionRate: number;
  };
  timeseries: Array<{ date: string; visitors: number; sessions: number; pageviews: number }>;
  topPages: Array<{ path: string; pageviews: number; visitors: number }>;
  topReferers: Array<{ host: string; sessions: number; conversionRate: number }>;
  topCampaigns: Array<{ source: string; medium: string; campaign: string; sessions: number; conversions: number }>;
  devices: Array<{ device: string; sessions: number; share: number }>;
  browsers: Array<{ browser: string; sessions: number }>;
  os: Array<{ os: string; sessions: number }>;
  geo: Array<{ country: string; region: string; city: string; sessions: number }>;
  funnel: {
    visits: number;
    productViews: number;
    checkoutStarts: number;
    purchases: number;
  };
  liveVisitors: number;
}

export async function getAdminAnalyticsOverview(days: number) {
  return request<AnalyticsOverview>(`/api/admin/analytics/overview?days=${days}`, {
    headers: authHeaders(),
  });
}

// ── Admin promotions ─────────────────────────────────────────────────

export interface AdminPromotion {
  id: string;
  name: string;
  description: string | null;
  scope: "GLOBAL" | "CATEGORY" | "PRODUCT";
  categoryId: string | null;
  productId: string | null;
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  displayOnHome: boolean;
  badgeLabel: string | null;
  badgeColor: string | null;
  priority: number;
  active: boolean;
  startsAt: string;
  endsAt: string;
  createdAt: string;
  updatedAt: string;
}

export type PromotionUpsert = Omit<AdminPromotion, "id" | "createdAt" | "updatedAt">;

export async function getAdminPromotions() {
  return request<AdminPromotion[]>("/api/admin/promotions", { headers: authHeaders() });
}

export async function getAdminPromotion(id: string) {
  return request<AdminPromotion>(`/api/admin/promotions/${id}`, { headers: authHeaders() });
}

export async function createAdminPromotion(payload: PromotionUpsert) {
  return request<{ id: string }>("/api/admin/promotions", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function updateAdminPromotion(id: string, payload: PromotionUpsert) {
  return request<void>(`/api/admin/promotions/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminPromotion(id: string) {
  return request<void>(`/api/admin/promotions/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// ── Admin business settings ──────────────────────────────────────────

export interface BusinessSettingsPayload {
  operating_hours: { from: string; to: string; timezone: string };
  same_day_cutoff: { enabled: boolean; cutoff: string; timezone: string };
  saturday_enabled: { enabled: boolean; from?: string; to?: string };
  holidays: string[];
  merchant_feed: { enabled: boolean; brand: string; currency: string; condition: "new" | "refurbished" | "used" };
}

export async function getAdminBusinessSettings() {
  return request<BusinessSettingsPayload>("/api/admin/business-settings", {
    headers: authHeaders(),
  });
}

export async function updateAdminBusinessSetting<K extends keyof BusinessSettingsPayload>(
  key: K,
  value: BusinessSettingsPayload[K]
) {
  return request<void>(`/api/admin/business-settings/${key}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(value),
  });
}

export interface PaginatedOrders {
  total: number;
  limit: number;
  offset: number;
  items: AdminOrder[];
}

export interface AdminOrdersQuery {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
}

export async function getAdminOrders(query: AdminOrdersQuery = {}) {
  const params = new URLSearchParams();
  if (query.limit) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  if (query.status) params.set("status", query.status);
  if (query.search) params.set("search", query.search);
  const qs = params.toString();
  return request<PaginatedOrders>(`/api/admin/orders${qs ? `?${qs}` : ""}`, {
    headers: authHeaders(),
  });
}

export type AdminOrderBulkAction =
  | "mark_paid"
  | "mark_processing"
  | "mark_completed"
  | "cancel"
  | "delete";

export async function deleteAdminOrder(id: string) {
  return request<void>(`/api/admin/orders/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

export interface BulkOrderResult {
  total: number;
  ok: number;
  failed: number;
  results: Array<{
    orderId: string;
    ok: boolean;
    newStatus?: string;
    gatewayAction?: string;
    error?: string;
  }>;
}

export async function bulkUpdateAdminOrders(
  orderIds: string[],
  action: AdminOrderBulkAction,
  notes?: string
) {
  return request<BulkOrderResult>("/api/admin/orders/bulk", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ orderIds, action, notes }),
  });
}

export interface AdminOrderDetail {
  id: string;
  orderCode: string;
  status: string;
  paymentStatus: string;
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  hasTokenShipping: boolean;
  shippingRuleName: string | null;
  billing: {
    name: string;
    email: string;
    phone: string;
    cpfCnpj: string;
  };
  shippingAddress: {
    recipientName: string | null;
    postalCode: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    district: string | null;
    city: string | null;
    state: string | null;
  } | null;
  notes: string | null;
  createdAt: string;
  payment: {
    billingType: string | null;
    invoiceUrl: string | null;
    bankSlipUrl: string | null;
    pixPayload: string | null;
    pixQrCode: string | null;
    dueDate: string | null;
    providerPaymentId: string | null;
  };
  items: Array<{
    productName: string;
    sku: string;
    productType: string;
    quantity: number;
    unitPriceCents: number;
    lineTotalCents: number;
  }>;
  history: Array<{
    status: string;
    paymentStatus: string | null;
    notes: string | null;
    createdAt: string;
  }>;
}

export async function getAdminOrderDetail(orderCode: string) {
  return request<AdminOrderDetail>(`/api/admin/orders/${encodeURIComponent(orderCode)}`, {
    headers: authHeaders(),
  });
}

export interface WhatsAppStatus {
  instance: string;
  state: "open" | "connecting" | "close" | "unknown";
  connected: boolean;
  ownerJid: string | null;
  profileName: string | null;
  profilePicUrl: string | null;
  number: string | null;
  messageCount: number | null;
  contactCount: number | null;
  chatCount: number | null;
  createdAt: string | null;
  lastUpdatedAt: string | null;
}

export async function getWhatsAppStatus() {
  return request<WhatsAppStatus>("/api/admin/whatsapp/status", {
    headers: authHeaders(),
  });
}

export interface WhatsAppConnectQr {
  qrcode: string | null;
  code: string | null;
  pairingCode: string | null;
}

export async function getWhatsAppConnectQr(phoneNumber?: string) {
  const qs = phoneNumber ? `?number=${encodeURIComponent(phoneNumber)}` : "";
  return request<WhatsAppConnectQr>(`/api/admin/whatsapp/connect${qs}`, {
    headers: authHeaders(),
  });
}

export async function disconnectWhatsApp() {
  return request<void>("/api/admin/whatsapp/disconnect", {
    method: "POST",
    headers: authHeaders(),
  });
}

export async function restartWhatsApp() {
  return request<void>("/api/admin/whatsapp/restart", {
    method: "POST",
    headers: authHeaders(),
  });
}

export async function deleteWhatsAppInstance() {
  return request<void>("/api/admin/whatsapp/instance", {
    method: "DELETE",
    headers: authHeaders(),
  });
}

// ── Audit log ─────────────────────────────────────────────────────────────
export interface AuditLogItem {
  id: string;
  actorType: string;
  actorId: string | null;
  entityType: string;
  entityId: string;
  action: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface PaginatedAuditLogs {
  total: number;
  limit: number;
  offset: number;
  items: AuditLogItem[];
}

export async function getAuditLogs(opts: { limit?: number; offset?: number; entityType?: string; action?: string } = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  if (opts.entityType) params.set("entityType", opts.entityType);
  if (opts.action) params.set("action", opts.action);
  const qs = params.toString();
  return request<PaginatedAuditLogs>(`/api/admin/audit${qs ? `?${qs}` : ""}`, {
    headers: authHeaders(),
  });
}

// ── Order status update ───────────────────────────────────────────────────
export async function updateOrderStatus(orderId: string, status: string, notes?: string) {
  return request<void>(`/api/admin/orders/${encodeURIComponent(orderId)}/status`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ status, notes }),
  });
}

// ── Customer (magic-link) auth ────────────────────────────────────────────
const CUSTOMER_TOKEN_KEY = "certi_customer_token";

export function getCustomerToken() {
  return localStorage.getItem(CUSTOMER_TOKEN_KEY);
}
export function setCustomerToken(token: string) {
  localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
}
export function clearCustomerToken() {
  localStorage.removeItem(CUSTOMER_TOKEN_KEY);
}

function customerHeaders() {
  const token = getCustomerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function requestCustomerLink(email: string) {
  return request<{ ok: boolean }>("/api/public/customer/request-link", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function exchangeCustomerToken(token: string) {
  return request<{ sessionToken: string; email: string }>("/api/public/customer/exchange", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export interface CustomerProfile {
  name: string;
  email: string;
  phone: string;
  cpf_cnpj: string;
}

export async function getCustomerMe() {
  return request<CustomerProfile>("/api/customer/me", {
    headers: customerHeaders(),
  });
}

export interface CustomerOrderItem {
  id: string;
  orderCode: string;
  status: string;
  paymentStatus: string;
  totalCents: number;
  createdAt: string;
  billingType: string | null;
  invoiceUrl: string | null;
}

export async function getCustomerOrders() {
  return request<CustomerOrderItem[]>("/api/customer/orders", {
    headers: customerHeaders(),
  });
}

// ── Blog (public) ────────────────────────────────────────────────────────
export interface BlogPostListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  coverImageUrl: string | null;
  keywords: string[];
  authorName: string;
  readingTimeMinutes: number | null;
  publishedAt: string | null;
}

export interface PaginatedBlogPosts {
  total: number;
  limit: number;
  offset: number;
  items: BlogPostListItem[];
}

export interface BlogPostFull extends BlogPostListItem {
  content: string;
  metaTitle: string | null;
  metaDescription: string | null;
}

export async function getPublicBlogPosts(
  opts: { limit?: number; offset?: number; keyword?: string | string[] } = {}
) {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  if (opts.keyword) {
    const kw = Array.isArray(opts.keyword) ? opts.keyword.join(",") : opts.keyword;
    if (kw.trim()) params.set("keyword", kw);
  }
  const qs = params.toString();
  return request<PaginatedBlogPosts>(`/api/public/blog${qs ? `?${qs}` : ""}`);
}

export async function getPublicBlogPost(slug: string) {
  return request<BlogPostFull>(`/api/public/blog/${encodeURIComponent(slug)}`);
}

// ── Lead capture ─────────────────────────────────────────────────────────
export interface LeadCapturePayload {
  email: string;
  name?: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

export async function captureLead(payload: LeadCapturePayload) {
  return request<{ ok: boolean }>("/api/public/leads/capture", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ── Cupons (admin) ───────────────────────────────────────────────────────
export type CouponDiscountType = "PERCENT" | "FIXED";
export type CouponStatusType = "ACTIVE" | "REDEEMED" | "EXPIRED" | "CANCELLED";

export interface CouponCampaign {
  id: string;
  name: string;
  codePrefix: string;
  discountType: CouponDiscountType;
  discountValue: number;
  validityHours: number;
  minOrderCents: number;
  active: boolean;
  isExitIntent: boolean;
  createdAt: string;
  updatedAt: string;
  issuedCount?: number;
  redeemedCount?: number;
}

export interface CouponCampaignInput {
  name: string;
  codePrefix: string;
  discountType: CouponDiscountType;
  discountValue: number;
  validityHours: number;
  minOrderCents: number;
  active: boolean;
  isExitIntent: boolean;
}

export interface AdminCoupon {
  id: string;
  campaignId: string | null;
  campaignName: string | null;
  code: string;
  email: string | null;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderCents: number;
  status: CouponStatusType;
  expiresAt: string;
  redeemedAt: string | null;
  redeemedOrderCode: string | null;
  createdAt: string;
}

export async function listCouponCampaigns() {
  return request<CouponCampaign[]>("/api/admin/coupon-campaigns", { headers: authHeaders() });
}

export async function createCouponCampaign(input: CouponCampaignInput) {
  return request<CouponCampaign>("/api/admin/coupon-campaigns", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
}

export async function updateCouponCampaign(id: string, input: Partial<CouponCampaignInput>) {
  return request<CouponCampaign>(`/api/admin/coupon-campaigns/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
}

export async function deleteCouponCampaign(id: string) {
  return request<void>(`/api/admin/coupon-campaigns/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

export async function listAdminCoupons(opts: { status?: CouponStatusType; campaignId?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.campaignId) params.set("campaignId", opts.campaignId);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; items: AdminCoupon[] }>(
    `/api/admin/coupons${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

// ── Leads (admin) ────────────────────────────────────────────────────────
export interface AdminLead {
  id: string;
  email: string;
  name: string | null;
  source: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export async function listAdminLeads(opts: { source?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.source) params.set("source", opts.source);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; items: AdminLead[] }>(
    `/api/admin/leads${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export async function resendLeadCoupon(email: string) {
  return request<CouponClaimResult>(
    `/api/admin/leads/${encodeURIComponent(email)}/resend-coupon`,
    { method: "POST", headers: authHeaders() }
  );
}

// ── Blog (admin) ─────────────────────────────────────────────────────────
export interface AdminBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  authorName: string;
  readingTimeMinutes: number | null;
  publishedAt: string | null;
  generatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getAdminBlogPosts(opts: { limit?: number; offset?: number; status?: string } = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  if (opts.status) params.set("status", opts.status);
  const qs = params.toString();
  return request<{ total: number; limit: number; offset: number; items: AdminBlogPost[] }>(
    `/api/admin/blog${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export interface AdminBlogPostFull extends AdminBlogPost {
  content: string;
  coverImageUrl: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  keywords: string[];
  generationPrompt: Record<string, unknown> | null;
}

export async function getAdminBlogPost(id: string) {
  return request<AdminBlogPostFull>(`/api/admin/blog/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
}

export interface BlogPostUpsert {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImageUrl?: string | null;
  metaTitle?: string;
  metaDescription?: string;
  keywords: string[];
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  authorName?: string;
  readingTimeMinutes?: number;
  generatedBy?: string | null;
  generationPrompt?: Record<string, unknown> | null;
}

export async function createAdminBlogPost(payload: BlogPostUpsert) {
  return request<{ id: string }>("/api/admin/blog", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function updateAdminBlogPost(id: string, payload: BlogPostUpsert) {
  return request<void>(`/api/admin/blog/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function deleteAdminBlogPost(id: string) {
  return request<void>(`/api/admin/blog/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

export async function setAdminBlogPostStatus(
  id: string,
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED"
) {
  return request<void>(`/api/admin/blog/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ status }),
  });
}

export async function regenerateAllBlogCovers(opts: {
  status?: "PUBLISHED" | "DRAFT" | "ARCHIVED" | "ALL";
  onlyMissing?: boolean;
} = {}) {
  return request<{ ok: boolean; queued: boolean; summary: string }>(
    "/api/admin/blog/regenerate-covers",
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(opts),
    }
  );
}

export async function generateBlogCover(id: string, overwrite = true) {
  return request<{ coverImageUrl: string; skipped?: boolean; reason?: string }>(
    `/api/admin/blog/${encodeURIComponent(id)}/generate-cover`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ overwrite }),
    }
  );
}

export interface GenerateArticleInput {
  topic: string;
  keywords?: string[];
  tone?: "informativo" | "vendedor" | "educativo" | "técnico";
  length?: "curto" | "médio" | "longo";
  audience?: string;
}

export interface GeneratedArticle {
  title: string;
  slug: string;
  excerpt: string;
  metaTitle: string;
  metaDescription: string;
  content: string;
  keywords: string[];
  readingTimeMinutes: number;
}

export async function generateBlogArticle(input: GenerateArticleInput) {
  return request<{ article: GeneratedArticle; generationPrompt: GenerateArticleInput }>(
    "/api/admin/blog/generate",
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(input),
    }
  );
}

// ── Blog automation (admin) ─────────────────────────────────────────────
export type BlogTopicStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export interface BlogTopic {
  id: string;
  tenantId: string;
  topic: string;
  keywords: string[];
  tone: "informativo" | "vendedor" | "educativo" | "técnico" | null;
  length: "curto" | "médio" | "longo" | null;
  audience: string | null;
  angleAHint: string | null;
  angleBHint: string | null;
  status: BlogTopicStatus;
  priority: number;
  attempts: number;
  lastError: string | null;
  postAId: string | null;
  postBId: string | null;
  createdAt: string;
  processedAt: string | null;
}

export interface BlogQueueStats {
  PENDING: number;
  PROCESSING: number;
  COMPLETED: number;
  FAILED: number;
  CANCELLED: number;
}

export type CarouselFormat = "single" | "carousel" | "auto";

export interface BlogSchedule {
  tenantId: string;
  enabled: boolean;
  paused: boolean;
  postsPerDay: number;
  weekdays: number[];
  preferredHour: number;
  preferredMinute: number;
  startDate: string | null;
  endDate: string | null;
  humanizeEnabled: boolean;
  instagramFormat: CarouselFormat;
  facebookFormat: CarouselFormat;
  lastRunAt: string | null;
  lastRunSummary: Record<string, unknown> | null;
  nextRunAt: string | null;
}

export async function listBlogTopics(opts: {
  status?: BlogTopicStatus | "ALL";
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (opts.status && opts.status !== "ALL") params.set("status", opts.status);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; items: BlogTopic[]; stats: BlogQueueStats }>(
    `/api/admin/blog-automation/topics${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export interface BlogTopicInput {
  topic: string;
  keywords?: string[];
  tone?: "informativo" | "vendedor" | "educativo" | "técnico";
  length?: "curto" | "médio" | "longo";
  audience?: string;
  angleAHint?: string;
  angleBHint?: string;
  priority?: number;
}

export async function createBlogTopics(payload: {
  topics?: BlogTopicInput[];
  lines?: string[];
}) {
  return request<{ created: number; rows: BlogTopic[] }>(
    "/api/admin/blog-automation/topics",
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }
  );
}

export async function updateBlogTopic(
  id: string,
  patch: Partial<BlogTopicInput> & { status?: BlogTopicStatus }
) {
  return request<void>(
    `/api/admin/blog-automation/topics/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify(patch),
    }
  );
}

export async function deleteBlogTopic(id: string) {
  return request<void>(
    `/api/admin/blog-automation/topics/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
}

export async function getBlogSchedule() {
  return request<BlogSchedule>("/api/admin/blog-automation/schedule", {
    headers: authHeaders(),
  });
}

export interface BlogScheduleUpdate {
  enabled?: boolean;
  paused?: boolean;
  postsPerDay?: number;
  weekdays?: number[];
  preferredHour?: number;
  preferredMinute?: number;
  startDate?: string | null;
  endDate?: string | null;
  humanizeEnabled?: boolean;
  instagramFormat?: CarouselFormat;
  facebookFormat?: CarouselFormat;
}

export async function updateBlogSchedule(patch: BlogScheduleUpdate) {
  return request<BlogSchedule>("/api/admin/blog-automation/schedule", {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
}

export async function runBlogAutomationNow() {
  return request<{ processed?: number; failed?: number; summary?: string }>(
    "/api/admin/blog-automation/run-now",
    {
      method: "POST",
      headers: authHeaders(),
    }
  );
}

// ── Social publishing (admin) ───────────────────────────────────────────
export type SocialPlatform = "LINKEDIN" | "INSTAGRAM" | "FACEBOOK" | "TIKTOK" | "X";

export type SocialPostStatus =
  | "PENDING"
  | "PUBLISHING"
  | "PUBLISHED"
  | "FAILED"
  | "SKIPPED"
  | "CANCELLED";

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  accountId: string;
  accountName: string | null;
  accountUrl: string | null;
  tokenExpiresAt: string | null;
  scopes: string[];
  metadata: Record<string, unknown>;
  enabled: boolean;
  connectedAt: string;
  lastUsedAt: string | null;
  lastError: string | null;
}

export interface SocialPlatformStatus {
  platform: SocialPlatform;
  configured: boolean;
  account: SocialAccount | null;
}

export async function getSocialAccounts() {
  return request<{ platforms: SocialPlatformStatus[] }>(
    "/api/admin/social/accounts",
    { headers: authHeaders() }
  );
}

export async function setSocialAccountEnabled(id: string, enabled: boolean) {
  return request<void>(
    `/api/admin/social/accounts/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify({ enabled }),
    }
  );
}

export async function disconnectSocialAccount(id: string) {
  return request<void>(
    `/api/admin/social/accounts/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    }
  );
}

export async function startSocialConnect(platform: SocialPlatform) {
  return request<{ url: string }>(
    `/api/admin/social/connect/${platform.toLowerCase()}`,
    {
      method: "POST",
      headers: authHeaders(),
    }
  );
}

export interface SocialPost {
  id: string;
  blogPostId: string;
  platform: SocialPlatform;
  status: SocialPostStatus;
  content: string;
  mediaUrls: string[];
  remoteId: string | null;
  remoteUrl: string | null;
  attempts: number;
  lastError: string | null;
  scheduledFor: string;
  publishedAt: string | null;
  createdAt: string;
  accountName: string | null;
}

export async function listSocialPosts(opts: {
  status?: SocialPostStatus;
  blogPostId?: string;
  limit?: number;
} = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.blogPostId) params.set("blogPostId", opts.blogPostId);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return request<{ items: SocialPost[] }>(
    `/api/admin/social/posts${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export async function retrySocialPost(id: string) {
  return request<void>(
    `/api/admin/social/posts/${encodeURIComponent(id)}/retry`,
    { method: "POST", headers: authHeaders() }
  );
}

export async function cancelSocialPost(id: string) {
  return request<void>(
    `/api/admin/social/posts/${encodeURIComponent(id)}/cancel`,
    { method: "POST", headers: authHeaders() }
  );
}

export async function fanoutBlogPost(blogPostId: string) {
  return request<{ enqueued: number; skipped: string[] }>(
    `/api/admin/social/fanout/${encodeURIComponent(blogPostId)}`,
    { method: "POST", headers: authHeaders() }
  );
}

export async function backfillSocialPosts(
  opts: { limit?: number; delayMs?: number } = {}
) {
  return request<{ ok: boolean; queued: boolean; summary: string }>(
    "/api/admin/social/backfill",
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(opts),
    }
  );
}

// ── Reels automation (admin) ────────────────────────────────────────────
export type ReelKind = "EDUCATIONAL" | "PROMOTIONAL";
export type ReelTopicStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "CANCELLED";
export type ReelStatus =
  | "PENDING"
  | "GENERATING_SCRIPT"
  | "GENERATING_IMAGES"
  | "GENERATING_AUDIO"
  | "RENDERING_VIDEO"
  | "UPLOADING"
  | "PUBLISHING"
  | "PUBLISHED"
  | "FAILED"
  | "CANCELLED";

export interface ReelTopic {
  id: string;
  kind: ReelKind;
  topic: string;
  hook: string | null;
  productId: string | null;
  audience: string | null;
  status: ReelTopicStatus;
  priority: number;
  attempts: number;
  lastError: string | null;
  reelId: string | null;
  createdAt: string;
}

export type VideoProvider =
  | "classic"
  | "cinematic-lite"
  | "cinematic-fast"
  | "cinematic-pro"
  | "heygen-avatar"
  | "alternate";

export interface ReelSchedule {
  tenantId: string;
  enabled: boolean;
  paused: boolean;
  educationalPerDay: number;
  promotionalPerDay: number;
  weekdays: number[];
  preferredHour: number;
  preferredMinute: number;
  startDate: string | null;
  endDate: string | null;
  publishInstagram: boolean;
  publishFacebook: boolean;
  draftMode: boolean;
  educationalProvider: VideoProvider;
  promotionalProvider: VideoProvider;
  alternateRotationCounter: number;
  lastRunAt: string | null;
}

export interface ReelRow {
  id: string;
  kind: ReelKind;
  status: ReelStatus;
  caption: string | null;
  mp4_url: string | null;
  audio_url: string | null;
  instagram_remote_url: string | null;
  facebook_remote_url: string | null;
  duration_s: number | null;
  attempts: number;
  last_error: string | null;
  generated_at: string | null;
  published_at: string | null;
  created_at: string;
}

export async function listReelTopics(opts: {
  kind?: ReelKind;
  status?: ReelTopicStatus;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (opts.kind) params.set("kind", opts.kind);
  if (opts.status) params.set("status", opts.status);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; items: ReelTopic[] }>(
    `/api/admin/reels/topics${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export interface ReelTopicInput {
  kind: ReelKind;
  topic: string;
  hook?: string;
  productId?: string;
  audience?: string;
  priority?: number;
}

export async function createReelTopics(payload: {
  topics?: ReelTopicInput[];
  lines?: string[];
  kind?: ReelKind;
}) {
  return request<{ created: number; rows: ReelTopic[] }>(
    "/api/admin/reels/topics",
    { method: "POST", headers: authHeaders(), body: JSON.stringify(payload) }
  );
}

export async function deleteReelTopic(id: string) {
  return request<void>(
    `/api/admin/reels/topics/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders() }
  );
}

export type PublicationPlatform =
  | "blog"
  | "instagram"
  | "facebook"
  | "linkedin"
  | "twitter"
  | "x"
  | "tiktok";

export type PublicationStatus = "success" | "failed" | "draft" | "pending" | "processing" | string;

export interface PublicationHistoryItem {
  platform: PublicationPlatform;
  status: PublicationStatus;
  title: string;
  url: string | null;
  error: string | null;
  at: string;
  sourceId: string;
  /** Qtd de mídias (quando >= 2 é carrossel). Opcional pra back-compat. */
  mediaCount?: number;
}

export async function getPublicationHistory(limit = 20) {
  return request<{ items: PublicationHistoryItem[] }>(
    `/api/admin/publication-history?limit=${limit}`,
    { headers: authHeaders() }
  );
}

export async function getReelSchedule() {
  return request<ReelSchedule>("/api/admin/reels/schedule", { headers: authHeaders() });
}

export interface ReelScheduleUpdate {
  enabled?: boolean;
  paused?: boolean;
  educationalPerDay?: number;
  promotionalPerDay?: number;
  weekdays?: number[];
  preferredHour?: number;
  preferredMinute?: number;
  startDate?: string | null;
  endDate?: string | null;
  publishInstagram?: boolean;
  publishFacebook?: boolean;
  draftMode?: boolean;
  educationalProvider?: VideoProvider;
  promotionalProvider?: VideoProvider;
}

export async function updateReelSchedule(patch: ReelScheduleUpdate) {
  return request<ReelSchedule>("/api/admin/reels/schedule", {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
}

export async function listReels(opts: {
  kind?: ReelKind;
  status?: ReelStatus;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (opts.kind) params.set("kind", opts.kind);
  if (opts.status) params.set("status", opts.status);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<{ total: number; items: ReelRow[] }>(
    `/api/admin/reels${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
}

export async function runReelsNow() {
  return request<{ ok: boolean; queued: boolean; summary: string }>(
    "/api/admin/reels/run-now",
    { method: "POST", headers: authHeaders() }
  );
}
